import React, {forwardRef, useEffect} from 'react';

import {useModal} from '../ModalProvider';
import {ModalCloseButtonView} from './ModalCloseButton.styles';

import type {HTMLAttributes, SpringTooling} from '../../__SharedTypes';

export interface ModalCloseButtonProps
  extends HTMLAttributes<HTMLButtonElement>, SpringTooling {
  /**
   * The busy state for screen readers.
   */
  ['aria-busy']?: boolean;
  /**
   * Determines if the close button should be positioned absolutely.
   *
   * This is to support existing usages of the CloseButton, while also enabling the new ModalActions wrapper.
   */
  _shouldUseLegacyPosition?: boolean;
}

export const ModalCloseButton = forwardRef<
  HTMLButtonElement,
  ModalCloseButtonProps
>(
  (
    {
      onClick,
      'data-automation-id': dataAutomationId = 'modal-close-button',
      _shouldUseLegacyPosition = true,
      ...props
    },
    ref
  ) => {
    const {onModalClose, setHasRenderedCloseButton} = useModal();

    useEffect(() => {
      setHasRenderedCloseButton(true);
      return () => {
        setHasRenderedCloseButton(false);
      };
    });

    const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      // @ts-expect-error - Floating-UI's Event type is not compatible with React's MouseEvent type.
      onModalClose(event, {reason: 'modal-close-button'});
      onClick && onClick(event);
    };

    return (
      <ModalCloseButtonView
        {...props}
        data-automation-id={dataAutomationId}
        data-modal-close-button
        density="comfortable"
        iconSize={16}
        onClick={handleOnClick}
        ref={ref}
        shouldUseLegacyPosition={_shouldUseLegacyPosition}
      />
    );
  }
);

ModalCloseButton.displayName = 'ModalCloseButton';
ModalCloseButton.toString = ModalCloseButtonView.toString;
